/* eslint-disable max-lines-per-function */
/* eslint-disable react-refresh/only-export-components */
import {
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';

export enum ContextSelectionType {
  Experiment = 'experiment',
  Product = 'product',
  Tool = 'tool',
}

export interface ContextSelectionItem {
  id: string;
  type: ContextSelectionType;
  title: string;
  selectedVersions?: string[];
  lastEditLabel: string;
  versionId?: string;
}

interface ContextSelectionContextValue {
  contextSelection: ContextSelectionItem[];
  setContextSelection: Dispatch<SetStateAction<ContextSelectionItem[]>>;
  addContextSelection: (item: ContextSelectionItem) => boolean;
  removeContextSelection: (selection: {
    id: string;
    type: ContextSelectionType;
  }) => void;
  clearContextSelection: () => void;
  setOnSelectItem: (
    handler: ((item: ContextSelectionItem) => void) | null
  ) => void;
  runOnSelectItem: (item: ContextSelectionItem) => void;
}

const ContextSelectionContext =
  createContext<ContextSelectionContextValue | null>(null);

interface ContextSelectionProviderProps {
  initialSelection?: ContextSelectionItem[];
}

export const ContextSelectionProvider = ({
  children,
  initialSelection = [],
}: PropsWithChildren<ContextSelectionProviderProps>) => {
  const [contextSelection, setContextSelection] =
    useState<ContextSelectionItem[]>(initialSelection);
  const onSelectItemRef = useRef<((item: ContextSelectionItem) => void) | null>(
    null
  );

  const setOnSelectItem = useCallback(
    (handler: ((item: ContextSelectionItem) => void) | null) => {
      onSelectItemRef.current = handler;
    },
    []
  );

  const runOnSelectItem = useCallback((item: ContextSelectionItem) => {
    onSelectItemRef.current?.(item);
  }, []);

  const addContextSelection = useCallback((item: ContextSelectionItem) => {
    let didAdd = false;

    setContextSelection((prev) => {
      if (
        prev.some(
          (existing) => existing.id === item.id && existing.type === item.type
        )
      ) {
        return prev;
      }

      didAdd = true;
      return [...prev, item];
    });

    return didAdd;
  }, []);

  const removeContextSelection = useCallback(
    (selection: { id: string; type: ContextSelectionType }) => {
      setContextSelection((prev) =>
        prev.filter(
          (item) => item.id !== selection.id || item.type !== selection.type
        )
      );
    },
    []
  );

  const clearContextSelection = useCallback(() => {
    setContextSelection([]);
  }, []);

  const value = useMemo(
    () => ({
      contextSelection,
      setContextSelection,
      addContextSelection,
      removeContextSelection,
      clearContextSelection,
      setOnSelectItem,
      runOnSelectItem,
    }),
    [
      contextSelection,
      setContextSelection,
      addContextSelection,
      removeContextSelection,
      clearContextSelection,
      setOnSelectItem,
      runOnSelectItem,
    ]
  );

  return (
    <ContextSelectionContext.Provider value={value}>
      {children}
    </ContextSelectionContext.Provider>
  );
};

export const useContextSelection = () => {
  const context = useContext(ContextSelectionContext);

  if (!context) {
    throw new Error(
      'useContextSelection must be used within a ContextSelectionProvider'
    );
  }

  return context;
};
